import { useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import ResumeUploadBox from "@components/dashboard/resume_upload_box.tsx";
import { Body } from "@sprout/typography.tsx";
import { IconButton } from "@sprout/icon_button.tsx";
import CloseIcon from "@sprout/icons/close.tsx";
import { useShowFlash } from "@sprout/flash.tsx";
import useTranslation from "@hooks/use_translation.ts";
import ModalFooter from "./modal_footer.tsx";
import type { SectionProps } from "./types.ts";

const ResumeSection = ({ data, onClose, source }: SectionProps) => {
  const { t } = useTranslation("user", { keyPrefix: "profile_checklist.modals.resume" });
  const { t: tu } = useTranslation("user");
  const queryClient = useQueryClient();
  const showFlash = useShowFlash();
  const [removing, setRemoving] = useState(false);
  const [uploadingFilename, setUploadingFilename] = useState<string | null>(null);
  const resume = data.resume;
  const filename = uploadingFilename ?? resume?.filename;

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await axios.delete("/resume");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["profile"] }),
        queryClient.invalidateQueries({ queryKey: ["whoami"] }),
      ]);
      showFlash(tu("resume.deleted"), "success");
    } catch {
      showFlash(tu("resume.error"), "error");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <Body>{t(resume ? "description_uploaded" : "description")}</Body>
        {filename && (
          <div className="flex items-center justify-between bg-[var(--green-50)] rounded-[5px] py-3 px-4">
            <Body>{filename}</Body>
            <IconButton
              icon={CloseIcon}
              size="sm"
              label={t("remove")}
              onClick={handleRemove}
              disabled={removing || Boolean(uploadingFilename)}
            />
          </div>
        )}
        <ResumeUploadBox
          onUploadComplete={onClose}
          onUploadingFilenameChange={setUploadingFilename}
          source={source === "profile" ? "profile_page" : "dashboard_profile_card"}
        />
      </div>
      <ModalFooter section="resume" onSave={onClose} onCancel={onClose} source={source} />
    </>
  );
};

export default ResumeSection;
